import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatRelativeTime } from './servicesShared';

// Services event bus (SERVICES_PLATFORM_PLAN.md §13): the catalog of event
// names an organization has seen (or that jobs subscribe to) and the recent
// feed. Subjects are a contact and/or an account — nothing else (D1).

export type EventSource = 'system' | 'platform' | 'server' | 'service';
export type EventTier = 'system' | 'server';

export interface EventDefinition {
    id: string;
    name: string;
    source: EventSource;
    tier: EventTier;
    description: string | null;
    property_notes: Record<string, unknown> | null;
    event_count: number;
    first_seen_at: string | null;
    last_seen_at: string | null;
    // Jobs with an active event routine on this name, grouped by service
    // (OWNED_JOBS_PLAN.md §1 Routine). A service's default routines decide
    // which events its jobs react to; there are no per-customer subscriptions.
    jobs: { service_id: string; service_key: string; service_name: string; jobs: number }[];
}

export interface ServiceEventRow {
    id: string;
    name: string;
    source: EventSource;
    tier: EventTier;
    payload: Record<string, unknown>;
    occurred_at: string | null;
    created_at: string | null;
    contact: { id: string; email: string | null; name: string | null } | null;
    account: { id: string; name: string | null; domain: string | null } | null;
}

const SOURCE_LABEL: Record<EventSource, string> = {
    system: 'Aimdoc',
    platform: 'Aimdoc',
    server: 'Your server',
    service: 'Service',
};

const SourceBadge: React.FC<{ source: EventSource }> = ({ source }) => (
    <Badge variant={source === 'server' ? 'default' : 'secondary'} className="font-normal">
        {SOURCE_LABEL[source] ?? source}
    </Badge>
);

const SubjectCell: React.FC<{ event: ServiceEventRow }> = ({ event }) => {
    const contact = event.contact;
    const account = event.account;
    if (!contact && !account) {
        return <span className="text-sm text-muted-foreground italic">Broadcast</span>;
    }
    return (
        <div className="flex flex-col gap-0.5 min-w-0">
            {contact && (
                <span className="text-sm truncate" title={contact.email ?? contact.id}>
                    {contact.name || contact.email || contact.id}
                </span>
            )}
            {account && (
                <span className="text-xs text-muted-foreground truncate" title={account.domain ?? account.id}>
                    {account.name || account.domain || account.id}
                </span>
            )}
        </div>
    );
};

const PayloadCell: React.FC<{ payload: Record<string, unknown> }> = ({ payload }) => {
    const keys = Object.keys(payload || {});
    if (!keys.length) return <span className="text-xs text-muted-foreground">—</span>;
    return (
        <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground select-none">
                {keys.length} {keys.length === 1 ? 'property' : 'properties'}
            </summary>
            <pre className="mt-1 max-h-48 overflow-auto rounded bg-muted p-2 font-mono text-[11px] leading-snug whitespace-pre-wrap break-all">
                {JSON.stringify(payload, null, 2)}
            </pre>
        </details>
    );
};

/** Events tab of the Services page: event catalog + recent log. Events are
 *  not a standalone concept — they exist to trigger services — so they live
 *  under Services. Own `view=` param (catalog | recent) so it does not collide
 *  with the Services page's `tab=`. */
export const EventsTab: React.FC<{ active: boolean }> = ({ active }) => {
    const { get, patch } = useApi();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('view');
    const activeTab = tabParam === 'recent' ? 'recent' : 'catalog';
    const nameFilter = searchParams.get('name') || '';

    const [loading, setLoading] = useState(true);
    const [serverEventsAvailable, setServerEventsAvailable] = useState(true);
    const [definitions, setDefinitions] = useState<EventDefinition[]>([]);

    const [eventsLoading, setEventsLoading] = useState(false);
    const [events, setEvents] = useState<ServiceEventRow[]>([]);
    const [eventsTotal, setEventsTotal] = useState(0);
    const [page, setPage] = useState(1);
    const perPage = 50;

    const [editing, setEditing] = useState<EventDefinition | null>(null);
    const [editDescription, setEditDescription] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (active) fetchDefinitions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    useEffect(() => {
        if (!active || activeTab !== 'recent') return;
        fetchEvents(page, nameFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, activeTab, page, nameFilter]);

    const fetchDefinitions = async () => {
        try {
            setLoading(true);
            const response = await get('/service-events/definitions');
            setServerEventsAvailable(Boolean(response.data.server_events_available));
            setDefinitions(response.data.data || []);
        } catch (error) {
            console.error('Error fetching event definitions:', error);
        }
        setLoading(false);
    };

    const fetchEvents = async (pageNumber: number, name: string) => {
        try {
            setEventsLoading(true);
            const params = new URLSearchParams({ page: String(pageNumber), per_page: String(perPage) });
            if (name) params.set('name', name);
            const response = await get(`/service-events?${params.toString()}`);
            setEvents(response.data.data || []);
            setEventsTotal(Number(response.data.total || 0));
        } catch (error) {
            console.error('Error fetching events:', error);
        }
        setEventsLoading(false);
    };

    const setTab = (tab: string) => {
        const params = new URLSearchParams(searchParams);
        if (tab === 'catalog') params.delete('view');
        else params.set('view', tab);
        setSearchParams(params);
        setPage(1);
    };

    const showRecentFor = (name: string) => {
        const params = new URLSearchParams(searchParams);
        params.set('view', 'recent');
        params.set('name', name);
        setSearchParams(params);
        setPage(1);
    };

    const setNameFilter = (value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) params.set('name', value);
        else params.delete('name');
        setSearchParams(params);
        setPage(1);
    };

    const openEdit = (definition: EventDefinition) => {
        setEditing(definition);
        setEditDescription(definition.description || '');
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            setSaving(true);
            const response = await patch(`/service-events/definitions/${editing.id}`, {
                description: editDescription,
            });
            setDefinitions((current) =>
                current.map((item) => (item.id === editing.id ? { ...item, ...response.data } : item))
            );
            setEditing(null);
        } catch (error) {
            console.error('Error saving event description:', error);
            toast.error('Failed to save description');
        }
        setSaving(false);
    };

    const totalPages = Math.max(1, Math.ceil(eventsTotal / perPage));
    const sortedDefinitions = useMemo(() => definitions, [definitions]);

    return (
        <div>
            <p className="text-sm text-slate-500 max-w-2xl">
                Moments your jobs can react to. Aimdoc emits events as contacts and accounts move
                through your agent; your own servers can send product events too. Events are free —
                only the wakes they trigger use credits. Subscribe a service to an event with an event
                routine in its Routines tab.
            </p>
            {!loading && !serverEventsAvailable && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
                    <TriangleAlert className="h-4 w-4 shrink-0" />
                    Sending events from your own servers needs an API key. Create one under Integrations → API
                    Keys.
                </div>
            )}
            <details className="mt-4 rounded-lg border bg-muted/30 px-4 py-3 text-sm">
                <summary className="cursor-pointer font-medium select-none">Send events from your server</summary>
                <p className="mt-2 text-muted-foreground">
                    One authenticated call. The first time you send a name it appears in the catalog below — describe
                    it there, then add an event routine on it to a service. Add <span className="font-mono">?dry_run=true</span>{' '}
                    while integrating to validate without storing anything.
                </p>
                <pre className="mt-3 overflow-x-auto rounded bg-muted p-3 font-mono text-[12px] leading-snug">
{`curl -X POST https://app.aimdoc.ai/api/v1/events \
  -H "Authorization: Bearer ak_your_api_key" \
  -H "Content-Type: application/json" \
  -d '{
    "name": "trial.expiring",
    "contact": { "external_id": "u_123", "email": "jason@acme.com" },
    "account": { "external_id": "acct_9", "domain": "acme.com" },
    "properties": { "days_left": 3 },
    "idempotency_key": "trial.expiring:u_123:2026-08-22"
  }'`}
                </pre>
                <p className="mt-2 text-xs text-muted-foreground">
                    Batch up to 100 with <span className="font-mono">{'{ "events": [...] }'}</span>. Full reference at{' '}
                    <a className="underline" href="/api/v1/docs" target="_blank" rel="noreferrer">
                        /api/v1/docs
                    </a>
                    .
                </p>
            </details>
            <Separator className="my-4" />

            <Tabs value={activeTab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="catalog">Catalog</TabsTrigger>
                    <TabsTrigger value="recent">Recent</TabsTrigger>
                </TabsList>

                <TabsContent value="catalog" className="mt-4">
                    <div className="border rounded-lg shadow overflow-hidden">
                        <Table className="table-fixed w-full">
                            <TableHeader>
                                <TableRow>
                                    <TableHead style={{ width: 260 }}>Name</TableHead>
                                    <TableHead style={{ width: 110 }}>Source</TableHead>
                                    <TableHead style={{ width: 90 }}>Events</TableHead>
                                    <TableHead style={{ width: 130 }}>Last seen</TableHead>
                                    <TableHead style={{ width: 240 }}>Jobs listening</TableHead>
                                    <TableHead>Description</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    [1, 2, 3].map((i) => (
                                        <TableRow key={i} className="animate-pulse">
                                            {[...Array(6)].map((_, j) => (
                                                <TableCell key={j} className="px-6 py-4">
                                                    <div className="h-4 bg-muted rounded w-3/4" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : sortedDefinitions.length ? (
                                    sortedDefinitions.map((definition) => (
                                        <TableRow key={definition.id} className="text-md">
                                            <TableCell className="overflow-hidden">
                                                <button
                                                    type="button"
                                                    className="text-sm font-mono text-left truncate block max-w-full hover:underline"
                                                    title={definition.name}
                                                    onClick={() => showRecentFor(definition.name)}
                                                >
                                                    {definition.name}
                                                </button>
                                            </TableCell>
                                            <TableCell>
                                                <SourceBadge source={definition.source} />
                                            </TableCell>
                                            <TableCell className="tabular-nums">{definition.event_count}</TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {definition.last_seen_at ? formatRelativeTime(definition.last_seen_at) : '—'}
                                            </TableCell>
                                            <TableCell className="overflow-hidden">
                                                <div className="flex flex-wrap items-center gap-1">
                                                    {(definition.jobs || []).length ? (
                                                        (definition.jobs || []).map((entry) => (
                                                            <Badge
                                                                key={entry.service_id}
                                                                variant="secondary"
                                                                className="font-normal truncate max-w-full cursor-pointer"
                                                                title={`${entry.jobs} job${entry.jobs === 1 ? '' : 's'} of ${entry.service_name} wake on this event`}
                                                                onClick={() => navigate(`/services/${entry.service_id}?tab=jobs`)}
                                                            >
                                                                {entry.service_name || entry.service_key} · {entry.jobs}
                                                            </Badge>
                                                        ))
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">—</span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="overflow-hidden">
                                                <div className="flex items-start gap-2">
                                                    <span className="text-sm text-muted-foreground line-clamp-2 grow">
                                                        {definition.description || <span className="italic">No description</span>}
                                                    </span>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 shrink-0"
                                                        onClick={() => openEdit(definition)}
                                                        aria-label={`Edit description for ${definition.name}`}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-24 text-center">
                                            No events yet. Aimdoc registers events as they happen — identify a contact or let a
                                            conversation go inactive and they will appear here.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </TabsContent>

                <TabsContent value="recent" className="mt-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Input
                            placeholder="Filter by event name"
                            value={nameFilter}
                            onChange={(event) => setNameFilter(event.target.value.trim())}
                            className="max-w-xs font-mono text-sm"
                        />
                        <span className="text-xs text-muted-foreground tabular-nums">
                            {eventsTotal} {eventsTotal === 1 ? 'event' : 'events'}
                        </span>
                    </div>
                    <div className="border rounded-lg shadow overflow-hidden">
                        <Table className="table-fixed w-full">
                            <TableHeader>
                                <TableRow>
                                    <TableHead style={{ width: 130 }}>When</TableHead>
                                    <TableHead style={{ width: 260 }}>Name</TableHead>
                                    <TableHead style={{ width: 220 }}>Subject</TableHead>
                                    <TableHead style={{ width: 110 }}>Source</TableHead>
                                    <TableHead>Properties</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {eventsLoading ? (
                                    [1, 2, 3].map((i) => (
                                        <TableRow key={i} className="animate-pulse">
                                            {[...Array(5)].map((_, j) => (
                                                <TableCell key={j} className="px-6 py-4">
                                                    <div className="h-4 bg-muted rounded w-3/4" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : events.length ? (
                                    events.map((event) => (
                                        <TableRow key={event.id} className="text-md align-top">
                                            <TableCell className="text-muted-foreground whitespace-nowrap">
                                                {formatRelativeTime(event.occurred_at || event.created_at)}
                                            </TableCell>
                                            <TableCell className="overflow-hidden">
                                                <span className="text-sm font-mono truncate block" title={event.name}>
                                                    {event.name}
                                                </span>
                                            </TableCell>
                                            <TableCell className="overflow-hidden">
                                                <SubjectCell event={event} />
                                            </TableCell>
                                            <TableCell>
                                                <SourceBadge source={event.source} />
                                            </TableCell>
                                            <TableCell className="overflow-hidden">
                                                <PayloadCell payload={event.payload} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} className="h-24 text-center">
                                            No events{nameFilter ? ` named ${nameFilter}` : ''} yet.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    {totalPages > 1 && (
                        <div className="flex items-center justify-end gap-2 mt-3 text-sm">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                                Previous
                            </Button>
                            <span className="text-muted-foreground tabular-nums">
                                {page} / {totalPages}
                            </span>
                            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                                Next
                            </Button>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-mono text-base">{editing?.name}</DialogTitle>
                        <DialogDescription>
                            Describe when this event fires and what its properties mean. Shown to anyone
                            authoring a service whose jobs react to it.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="event-description">Description</Label>
                        <Textarea
                            id="event-description"
                            rows={4}
                            value={editDescription}
                            onChange={(event) => setEditDescription(event.target.value)}
                            placeholder="e.g. Fires when a trial is 3 days from expiring. Properties: days_left."
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button onClick={saveEdit} disabled={saving}>
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EventsTab;
